"use client";
import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { getXAxisTickInterval } from "../utils";
import BMView from "../../View/BMView";

import {
  AnalyticsLineChart,
  ChartTooltipProps,
  LineSeries,
} from "../AnalyticsLineChart";
import { ViewType } from "../types";
import { FlightBookingChangesResult } from "./types";

type Props = {
  data: FlightBookingChangesResult;
  viewType?: ViewType;
  interactive?: boolean;
};

function getQuarterFromMonth(month: string) {
  const monthIndex = new Date(`${month} 1, 2000`).getMonth();
  if (Number.isNaN(monthIndex)) return undefined;
  return `Q${Math.floor(monthIndex / 3) + 1}`;
}

function getBucketKey(
  t: { month: string; quarter?: string; year: string },
  viewType: ViewType
) {
  if (viewType === "QUARTERLY") {
    const quarter = t.quarter ?? getQuarterFromMonth(t.month);
    return `${quarter} ${t.year}`;
  }
  if (viewType === "ANNUALLY") return t.year;
  return `${t.month} ${t.year}`;
}

export default function FlightBookingChangesLineChart({
  data,
  viewType = "MONTHLY",
  interactive = true,
}: Props) {
  const { CorporateTransactionDataAnalytics: cr } = useMobileContentResource();

  const { transactions } = data.flightBookingChanges;

  const chartData = useMemo(() => {
    const buckets = new Map<
      string,
      {
        canceled_without_refund: number;
        canceled_with_refund: number;
        rescheduled: number;
      }
    >();
    const sortDates = new Map<string, string>();

    transactions.forEach((t) => {
      const key = getBucketKey(t, viewType);
      const prev = buckets.get(key);
      buckets.set(key, {
        canceled_without_refund:
          (prev?.canceled_without_refund ?? 0) +
          Number(t.canceled_without_refund_transactions),
        canceled_with_refund:
          (prev?.canceled_with_refund ?? 0) +
          Number(t.canceled_with_refund_transactions),
        rescheduled:
          (prev?.rescheduled ?? 0) + Number(t.rescheduled_transactions),
      });
      const candidateDate =
        t.roundedDownTransactionDate ?? `${t.month} 1, ${t.year}`;
      const existingDate = sortDates.get(key);
      if (
        !existingDate ||
        new Date(candidateDate).getTime() < new Date(existingDate).getTime()
      ) {
        sortDates.set(key, candidateDate);
      }
    });

    return Array.from(buckets.entries())
      .map(([date, values]) => ({ date, ...values }))
      .sort(
        (a, b) =>
          new Date(sortDates.get(a.date) ?? 0).getTime() -
          new Date(sortDates.get(b.date) ?? 0).getTime()
      );
  }, [transactions, viewType]);

  const series: LineSeries[] = useMemo(
    () => [
      {
        key: "canceled_without_refund",
        color: BMPrimitives.BMBrand.productFlights,
        label: cr.cancellationWithoutRefunds,
      },
      {
        key: "canceled_with_refund",
        color: BMPrimitives.BMBrand.productHotels,
        label: cr.cancellationWithRefunds,
      },
      { key: "rescheduled", color: "#8c8c8c", label: cr.rescheduledFlights },
    ],
    [cr]
  );

  const { yMin, yAxisDomain, yAxisTicks } = useMemo<{
    yMin: number;
    yAxisDomain: [number, number];
    yAxisTicks: number[];
  }>(() => {
    const allValues = transactions.flatMap((t) => [
      Number(t.canceled_without_refund_transactions),
      Number(t.canceled_with_refund_transactions),
      Number(t.rescheduled_transactions),
    ]);

    const [maxValue, minValue] =
      allValues.length > 0
        ? [Math.max(...allValues), Math.min(...allValues)]
        : [0, 0];
    const [rawMax, rawMin] = [maxValue * 1.05, minValue * 0.5];

    if (rawMax <= 0) {
      return {
        yMin: 0,
        yAxisDomain: [0, 1],
        yAxisTicks: [0, 0.2, 0.4, 0.6, 0.8, 1],
      };
    }

    const magMax = Math.pow(10, Math.floor(Math.log10(rawMax)) - 1);
    const magMin =
      rawMin > 0 ? Math.pow(10, Math.floor(Math.log10(rawMin)) - 1) : magMax;
    const yMax = Math.ceil(rawMax / magMax) * magMax;
    const yMin = rawMin > 0 ? Math.floor(rawMin / magMin) * magMin : 0;

    return {
      yMin,
      yAxisDomain: [yMin, yMax],
      yAxisTicks: Array.from(
        { length: 6 },
        (_, i) => yMin + ((yMax - yMin) / 5) * i
      ),
    };
  }, [transactions]);

  const formatYAxisTick = useMemo(
    () => (v: string | number) => {
      if (v === yMin) return "";
      return new Intl.NumberFormat("id-ID").format(Math.round(Number(v)));
    },
    [yMin]
  );

  const formatXAxisTick = useCallback(
    (v: string | number) => {
      const str = String(v);
      if (viewType === "ANNUALLY") return str;

      const [period, year] = str.split(" ");
      if (viewType === "QUARTERLY") return `${period} '${year.slice(2)}`;
      return `${period.slice(0, 3)} '${year.slice(2)}`;
    },
    [viewType]
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: ChartTooltipProps) => {
      if (!interactive || !active || !payload?.length) return null;
      return (
        <FlightBookingTooltip label={label} payload={payload} series={series} />
      );
    },
    [series, interactive]
  );

  return (
    <AnalyticsLineChart
      data={chartData}
      series={series}
      yAxisWidth={17}
      yAxisTicks={yAxisTicks}
      yAxisDomain={yAxisDomain}
      formatYAxisTick={formatYAxisTick}
      formatXAxisTick={formatXAxisTick}
      xAxisInterval={getXAxisTickInterval(viewType, chartData.length)}
      renderTooltip={renderTooltip}
    />
  );
}

type FlightBookingTooltipProps = {
  label?: string;
  payload: NonNullable<ChartTooltipProps["payload"]>;
  series: LineSeries[];
};

function FlightBookingTooltip({
  label,
  payload,
  series,
}: FlightBookingTooltipProps) {
  const { BMComponents } = useBMTheme();
  const styles = useMemo(() => createStyles(BMComponents), [BMComponents]);

  const { full, range } = useMemo(() => {
    const [month, year] = label?.split(" ") ?? [];
    if (!month || !year) return { full: "", range: "" };

    const daysInMonth = new Date(
      Number(year),
      new Date(`${month} 1`).getMonth() + 1,
      0
    ).getDate();

    return { full: label, range: `(1 - ${daysInMonth} ${month.slice(0, 3)})` };
  }, [label]);

  const colorByKey = useMemo(() => {
    const map: Record<string, string> = {};
    series.forEach((s) => {
      map[s.key] = s.color;
    });
    return map;
  }, [series]);

  return (
    <BMView style={styles.container} spacing="md">
      {full || range ? (
        <BMView row align="center" spacing="xs">
          <BMText variant="uiSmall" ink="BMStyleTextInkSecondary">
            {full}
          </BMText>
          <BMText variant="uiSmallLi" ink="BMStyleTextInkSecondary">
            {range}
          </BMText>
        </BMView>
      ) : null}
      <BMView spacing="xs">
        {payload.map((entry) => {
          const dataKey =
            typeof entry.dataKey === "string" ? entry.dataKey : "";
          const circleColor = colorByKey[dataKey] ?? "#999";

          return (
            <BMView key={entry.dataKey} row align="center" spacing="xs">
              <BMView
                style={[styles.circle, { backgroundColor: circleColor }]}
              />
              <BMText variant="paragraphSmall" ink="BMStyleTextInkSecondary">
                {String(entry.name)}:
              </BMText>
              <BMText variant="paragraphSmallEm">{String(entry.value)}x</BMText>
            </BMView>
          );
        })}
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      padding: BMPrimitives.BMSize.sm,
      borderRadius: BMPrimitives.BMRadius.md,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolor,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    circle: {
      width: BMPrimitives.BMSize.sm,
      height: BMPrimitives.BMSize.sm,
      borderRadius: BMPrimitives.BMRadius.rounded,
    },
  });
}
